(() => {
  'use strict';

  const SELECTORS = {
    headerSection: '.swan-pro-header-section',
    navDetails: '[data-swan-nav-details]',
    mobileDrawer: '[data-swan-mobile-drawer]',
    mobileOverlay: '[data-swan-mobile-overlay]',
    mobileOpen: '[data-swan-mobile-open]',
    mobileClose: '[data-swan-mobile-close]',
    stickyMenu: '[data-swan-sticky-menu]',
    languageForm: '[data-swan-language-form]',
    languageDetails: '[data-swan-language-details]',
    languageOption: '[data-swan-language-option]',
    languageInput: '[data-swan-language-input]'
  };

  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const debounce = (callback, delay = 180) => {
    let timeoutId;

    return (...args) => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => callback(...args), delay);
    };
  };

  class SwanPredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.dataset.initialized = 'true';
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-swan-search-results]');
      this.clearButton = this.querySelector('[data-swan-search-clear]');
      this.status = this.querySelector('[data-swan-search-status]');
      this.searchUrl = this.dataset.searchUrl || window.theme?.routes?.predictiveSearch || '/search/suggest';
      this.resultsLimit = Math.min(Math.max(Number.parseInt(this.dataset.resultsLimit || '8', 10), 1), 10);
      this.minCharacters = Math.max(Number.parseInt(this.dataset.minCharacters || '2', 10), 1);
      this.cache = new Map();
      this.abortController = null;
      this.activeIndex = -1;
      this.options = [];
      this.lastRequestedTerm = '';
      this.renderedTerm = '';

      if (!this.form || !this.input || !this.results) return;

      this.debouncedSearch = debounce((term) => this.getResults(term), 180);

      this.input.addEventListener('input', () => this.handleInput());
      this.input.addEventListener('focus', () => this.handleFocus());
      this.input.addEventListener('keydown', (event) => this.handleKeydown(event));
      this.form.addEventListener('submit', (event) => this.handleSubmit(event));

      this.clearButton?.addEventListener('click', () => this.clearSearch());

      this.addEventListener('focusout', () => {
        window.setTimeout(() => {
          if (!this.contains(document.activeElement)) this.close();
        }, 0);
      });

      this.handleInput({ fetchResults: false });
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    normalizeTerm(value = this.input?.value || '') {
      return value.trim().replace(/\s+/g, ' ');
    }

    handleInput(options = {}) {
      const { fetchResults = true } = options;
      const term = this.normalizeTerm();

      if (this.clearButton) this.clearButton.hidden = term.length === 0;
      this.resetActiveOption();

      if (term.length < this.minCharacters) {
        this.abortController?.abort();
        this.lastRequestedTerm = '';
        this.close({ clearMarkup: true });
        this.announce(term.length === 0 ? '' : `Type ${this.minCharacters - term.length} more character${this.minCharacters - term.length === 1 ? '' : 's'} to see product suggestions.`);
        return;
      }

      if (this.renderedTerm && this.renderedTerm !== term.toLocaleLowerCase()) this.close();
      if (fetchResults) this.debouncedSearch(term);
    }

    handleFocus() {
      const term = this.normalizeTerm();
      if (term.length < this.minCharacters) return;

      const cacheKey = term.toLocaleLowerCase();
      if (this.cache.has(cacheKey)) {
        this.renderResults(this.cache.get(cacheKey), term);
      } else if (this.renderedTerm === cacheKey && this.results.innerHTML.trim()) {
        this.open();
      } else {
        this.debouncedSearch(term);
      }
    }

    handleSubmit(event) {
      const term = this.normalizeTerm();

      if (!term) {
        event.preventDefault();
        this.input.focus();
        return;
      }

      if (this.activeIndex >= 0 && this.options[this.activeIndex]) {
        event.preventDefault();
        this.options[this.activeIndex].click();
      }
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        if (this.dataset.open === 'true') {
          event.preventDefault();
          this.close();
          this.input.focus();
        }
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (this.dataset.open !== 'true') this.handleFocus();
        this.moveActiveOption(1);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.moveActiveOption(-1);
        return;
      }

      if (event.key === 'Enter' && this.activeIndex >= 0 && this.options[this.activeIndex]) {
        event.preventDefault();
        this.options[this.activeIndex].click();
      }
    }

    async getResults(term) {
      const normalizedTerm = this.normalizeTerm(term);
      const cacheKey = normalizedTerm.toLocaleLowerCase();

      if (normalizedTerm.length < this.minCharacters) return;

      if (this.cache.has(cacheKey)) {
        this.renderResults(this.cache.get(cacheKey), normalizedTerm);
        return;
      }

      this.abortController?.abort();
      this.abortController = new AbortController();
      this.lastRequestedTerm = normalizedTerm;
      this.setLoading(true);
      this.announce('Loading product suggestions.');

      const params = new URLSearchParams({
        q: normalizedTerm,
        'resources[type]': 'product',
        'resources[limit]': String(this.resultsLimit),
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,variants.title,variants.sku',
        section_id: 'swan-predictive-search'
      });

      try {
        const response = await fetch(`${this.searchUrl}?${params.toString()}`, {
          headers: { Accept: 'text/html' },
          signal: this.abortController.signal
        });

        if (!response.ok) throw new Error(`Predictive search request failed with status ${response.status}`);

        const text = await response.text();
        const documentFragment = new DOMParser().parseFromString(text, 'text/html');
        const content = documentFragment.querySelector('[data-swan-predictive-content]');

        if (!content) throw new Error('Predictive search response did not contain the expected section markup.');

        const markup = content.outerHTML;
        this.cache.set(cacheKey, markup);

        if (this.normalizeTerm() === normalizedTerm) {
          this.renderResults(markup, normalizedTerm);
        }
      } catch (error) {
        if (error.name === 'AbortError') return;

        console.error('[Swan header search]', error);
        this.close({ clearMarkup: true });
        this.announce('Search suggestions are temporarily unavailable. Press Enter to search the store.');
      } finally {
        if (this.lastRequestedTerm === normalizedTerm) this.setLoading(false);
      }
    }

    renderResults(markup, term) {
      if (this.normalizeTerm() !== term) return;

      this.results.innerHTML = markup;
      this.renderedTerm = term.toLocaleLowerCase();
      this.refreshOptions();
      this.open();

      const productCount = this.results.querySelectorAll('.swan-search-result').length;
      this.announce(productCount > 0
        ? `${productCount} product suggestion${productCount === 1 ? '' : 's'} available. Use the up and down arrow keys to review them.`
        : 'No matching products found. Press Enter to search the store.');
    }

    refreshOptions() {
      this.options = Array.from(this.results.querySelectorAll('[data-swan-search-option]'));
      this.activeIndex = -1;

      this.options.forEach((option, index) => {
        option.id = option.id || `${this.input.id}-option-${index}`;
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', 'false');
        option.addEventListener('mousemove', () => this.setActiveOption(index));
      });
    }

    moveActiveOption(direction) {
      if (this.dataset.open !== 'true' || this.options.length === 0) return;

      let nextIndex = this.activeIndex + direction;
      if (nextIndex < 0) nextIndex = this.options.length - 1;
      if (nextIndex >= this.options.length) nextIndex = 0;

      this.setActiveOption(nextIndex, true);
    }

    setActiveOption(index, scrollIntoView = false) {
      this.options.forEach((option, optionIndex) => {
        const active = optionIndex === index;
        option.classList.toggle('is-keyboard-active', active);
        option.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      this.activeIndex = index;
      const activeOption = this.options[index];

      if (activeOption) {
        this.input.setAttribute('aria-activedescendant', activeOption.id);
        if (scrollIntoView) activeOption.scrollIntoView({ block: 'nearest' });
      }
    }

    resetActiveOption() {
      this.options?.forEach((option) => {
        option.classList.remove('is-keyboard-active');
        option.setAttribute('aria-selected', 'false');
      });
      this.activeIndex = -1;
      this.input?.removeAttribute('aria-activedescendant');
    }

    clearSearch() {
      this.abortController?.abort();
      this.input.value = '';
      this.handleInput({ fetchResults: false });
      this.input.focus();
    }

    open() {
      if (!this.results.innerHTML.trim()) return;

      this.results.hidden = false;
      this.dataset.open = 'true';
      this.input.setAttribute('aria-expanded', 'true');
    }

    close(options = {}) {
      const { clearMarkup = false } = options;

      this.dataset.open = 'false';
      this.input?.setAttribute('aria-expanded', 'false');
      this.results.hidden = true;
      this.resetActiveOption();

      if (clearMarkup) {
        this.results.innerHTML = '';
        this.renderedTerm = '';
      }
    }

    setLoading(isLoading) {
      this.classList.toggle('is-loading', isLoading);
      this.input?.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    announce(message) {
      if (this.status) this.status.textContent = message;
    }
  }

  if (!customElements.get('swan-predictive-search')) {
    customElements.define('swan-predictive-search', SwanPredictiveSearch);
  }

  const closeDesktopMenus = (section, except = null) => {
    section.querySelectorAll(SELECTORS.navDetails).forEach((details) => {
      if (details === except) return;
      details.removeAttribute('open');
      details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
    });
  };

  const closeLanguageMenus = (root = document, except = null) => {
    root.querySelectorAll?.(`${SELECTORS.languageDetails}[open]`).forEach((details) => {
      if (details === except) return;
      details.removeAttribute('open');
      details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
    });
  };

  const initializeLanguageForms = (section) => {
    section.querySelectorAll(SELECTORS.languageForm).forEach((form) => {
      if (form.dataset.swanLanguageInitialized === 'true') return;

      const input = form.querySelector(SELECTORS.languageInput);
      const options = Array.from(form.querySelectorAll(SELECTORS.languageOption));
      const details = form.querySelector(SELECTORS.languageDetails);
      const summary = details?.querySelector(':scope > summary');

      if (!input || options.length === 0) return;
      form.dataset.swanLanguageInitialized = 'true';

      options.forEach((option) => {
        option.addEventListener('click', (event) => {
          event.preventDefault();
          const locale = option.dataset.locale;

          if (!locale || locale === input.value) {
            if (details) {
              closeLanguageMenus(section);
              summary?.focus();
            }
            return;
          }

          input.value = locale;
          form.classList.add('is-submitting');
          form.submit();
        });
      });

      if (!details || !summary) return;

      summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');

      details.addEventListener('toggle', () => {
        summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');

        if (details.open) {
          closeDesktopMenus(section);
          closeLanguageMenus(document, details);
          document.querySelectorAll('swan-predictive-search').forEach((search) => search.close?.());
        }
      });

      details.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !details.open) return;

        event.preventDefault();
        closeLanguageMenus(section);
        summary.focus();
      });

      details.addEventListener('focusout', () => {
        window.setTimeout(() => {
          if (!details.matches(':focus-within')) closeLanguageMenus(section);
        }, 0);
      });
    });
  };

  const initializeDesktopNavigation = (section) => {
    const detailsElements = Array.from(section.querySelectorAll(SELECTORS.navDetails));
    if (detailsElements.length === 0) return;

    const hoverEnabled = section.querySelector('.swan-pro-header-wrapper')?.dataset.hoverMenu !== 'false';
    const hoverMedia = window.matchMedia('(min-width: 900px) and (hover: hover) and (pointer: fine)');
    let openTimer;
    let closeTimer;

    const openDetails = (details) => {
      window.clearTimeout(closeTimer);
      closeDesktopMenus(section, details);
      details.setAttribute('open', '');
      details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'true');
    };

    const closeDetails = (details) => {
      details.removeAttribute('open');
      details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
    };

    detailsElements.forEach((details) => {
      const summary = details.querySelector(':scope > summary');
      if (!summary) return;

      summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');

      details.addEventListener('toggle', () => {
        summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
        if (details.open) closeDesktopMenus(section, details);
      });

      summary.addEventListener('click', () => {
        window.clearTimeout(openTimer);
        window.clearTimeout(closeTimer);
        if (!details.open) closeDesktopMenus(section, details);
      });

      details.addEventListener('mouseenter', () => {
        if (!hoverEnabled || !hoverMedia.matches) return;
        window.clearTimeout(closeTimer);
        openTimer = window.setTimeout(() => openDetails(details), 90);
      });

      details.addEventListener('mouseleave', () => {
        if (!hoverEnabled || !hoverMedia.matches) return;
        window.clearTimeout(openTimer);
        closeTimer = window.setTimeout(() => closeDetails(details), 180);
      });

      details.addEventListener('focusout', () => {
        window.setTimeout(() => {
          if (!details.matches(':focus-within') && !details.matches(':hover')) closeDetails(details);
        }, 0);
      });
    });

    section.addEventListener('click', (event) => {
      if (!event.target.closest(SELECTORS.navDetails)) closeDesktopMenus(section);
    });

    section.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;

      const openDetailsElement = detailsElements.find((details) => details.open);
      if (!openDetailsElement) return;

      event.preventDefault();
      closeDetails(openDetailsElement);
      openDetailsElement.querySelector(':scope > summary')?.focus();
    });

    hoverMedia.addEventListener?.('change', () => closeDesktopMenus(section));
  };

  const initializeMobileDrawer = (section) => {
    const drawer = section.querySelector(SELECTORS.mobileDrawer);
    const overlay = section.querySelector(SELECTORS.mobileOverlay);
    const openButtons = Array.from(section.querySelectorAll(SELECTORS.mobileOpen));
    const closeButton = section.querySelector(SELECTORS.mobileClose);
    const stickyMenu = section.querySelector(SELECTORS.stickyMenu);

    if (!drawer || !overlay || openButtons.length === 0 || !closeButton) return;

    let lastFocusedElement = null;
    let closeTimeout;

    const isOpen = () => drawer.classList.contains('is-open');

    const setOpenButtonState = (expanded) => {
      openButtons.forEach((button) => {
        button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        button.setAttribute('aria-label', expanded ? 'Close menu' : 'Open menu');
      });
    };

    const setPageLock = (locked) => {
      document.documentElement.classList.toggle('swan-pro-mobile-menu-open', locked);
      document.body.classList.toggle('swan-pro-mobile-menu-open', locked);
      section.classList.toggle('is-mobile-menu-open', locked);
      stickyMenu?.classList.toggle('is-drawer-open', locked);
    };

    const getFocusableElements = () => Array.from(drawer.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter((element) => !element.hasAttribute('hidden') && element.offsetParent !== null);

    const openDrawer = (event) => {
      window.clearTimeout(closeTimeout);
      closeDesktopMenus(section);
      closeLanguageMenus(section);
      document.querySelectorAll('swan-predictive-search').forEach((search) => search.close?.());

      lastFocusedElement = event?.currentTarget instanceof HTMLElement
        ? event.currentTarget
        : document.activeElement;
      drawer.hidden = false;
      overlay.hidden = false;
      drawer.setAttribute('aria-hidden', 'false');
      setOpenButtonState(true);
      setPageLock(true);

      window.requestAnimationFrame(() => {
        overlay.classList.add('is-open');
        drawer.classList.add('is-open');
        window.setTimeout(() => closeButton.focus(), 50);
      });
    };

    const closeDrawer = (options = {}) => {
      const { restoreFocus = true, immediate = false } = options;
      if (!isOpen() && drawer.hidden) return;

      drawer.classList.remove('is-open');
      overlay.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      setOpenButtonState(false);
      setPageLock(false);

      const finishClose = () => {
        if (isOpen()) return;
        drawer.hidden = true;
        overlay.hidden = true;
        if (restoreFocus && lastFocusedElement instanceof HTMLElement) lastFocusedElement.focus();
      };

      if (immediate) {
        finishClose();
      } else {
        closeTimeout = window.setTimeout(finishClose, 270);
      }
    };

    openButtons.forEach((button) => button.addEventListener('click', (event) => {
      if (isOpen()) {
        closeDrawer();
      } else {
        openDrawer(event);
      }
    }));
    closeButton.addEventListener('click', () => closeDrawer());
    overlay.addEventListener('click', () => closeDrawer());

    drawer.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (link) closeDrawer({ restoreFocus: false });
    });

    drawer.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeDrawer();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) {
        event.preventDefault();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    });

    const desktopMedia = window.matchMedia('(min-width: 900px)');
    const handleViewportChange = (event) => {
      if (event.matches) closeDrawer({ restoreFocus: false, immediate: true });
    };

    desktopMedia.addEventListener?.('change', handleViewportChange);
  };

  const initializeStickyMenu = (section) => {
    const stickyMenu = section.querySelector(SELECTORS.stickyMenu);
    if (!stickyMenu) return;

    const mobileMedia = window.matchMedia('(max-width: 899px)');
    const mobileEnabled = stickyMenu.dataset.mobileEnabled === 'true';
    const desktopEnabled = stickyMenu.dataset.desktopEnabled === 'true';
    let scheduled = false;

    const updateVisibility = () => {
      scheduled = false;

      const showOnMobile = mobileMedia.matches && (mobileEnabled || desktopEnabled);
      const showOnDesktop = !mobileMedia.matches && desktopEnabled && window.scrollY > 100;
      const shouldShow = showOnMobile || showOnDesktop;

      stickyMenu.classList.toggle('is-visible', shouldShow);
      document.body.classList.toggle('swan-pro-has-mobile-sticky-menu', showOnMobile);
    };

    const scheduleUpdate = () => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(updateVisibility);
    };

    updateVisibility();
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate, { passive: true });
    mobileMedia.addEventListener?.('change', scheduleUpdate);
  };

  const initializeHeaderSection = (section) => {
    if (!section || section.dataset.swanHeaderInitialized === 'true') return;

    section.dataset.swanHeaderInitialized = 'true';
    initializeLanguageForms(section);
    initializeDesktopNavigation(section);
    initializeMobileDrawer(section);
    initializeStickyMenu(section);
  };

  const initializeAllHeaders = (root = document) => {
    if (root.matches?.(SELECTORS.headerSection)) initializeHeaderSection(root);
    root.querySelectorAll?.(SELECTORS.headerSection).forEach(initializeHeaderSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAllHeaders());
  } else {
    initializeAllHeaders();
  }

  document.addEventListener('click', (event) => {
    document.querySelectorAll('swan-predictive-search[data-open="true"]').forEach((search) => {
      if (!search.contains(event.target)) search.close?.();
    });

    document.querySelectorAll(`${SELECTORS.navDetails}[open]`).forEach((details) => {
      if (details.contains(event.target)) return;
      details.removeAttribute('open');
      details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
    });

    document.querySelectorAll(`${SELECTORS.languageDetails}[open]`).forEach((details) => {
      if (details.contains(event.target)) return;
      details.removeAttribute('open');
      details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
    });
  });

  document.addEventListener('shopify:section:load', (event) => initializeAllHeaders(event.target));
})();
